import { Link, useLocation } from "wouter";
import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Menu, Shield, LogOut } from "lucide-react";
import { useState } from "react";
import { useLocale } from "@/contexts/LocaleContext";
import LanguageSwitcher from "./LanguageSwitcher";

export default function Navbar() {
  const [location] = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const isAdmin = user?.role === "admin";
  const { t, locale } = useLocale();
  const buyHref = (locale === "en" ? "/about-the-book" : "/sobre-el-libro") + "#opciones";

  const navLinks = [
    { href: locale === "en" ? "/en" : "/", label: t("nav.home") },
    { href: "/blog", label: t("nav.blog") },
    { href: "/ideas", label: t("nav.ideas") },
    { href: locale === "en" ? "/about-the-book" : "/sobre-el-libro", label: t("nav.about") },
  ];

  return (
    <header className="sticky top-0 z-50 border-b border-border/50 bg-navy-dark/95 backdrop-blur-md">
      <div className="container flex items-center justify-between h-16">
        {/* Logo */}
        <Link href={locale === "en" ? "/en" : "/"} className="flex items-center gap-2 group">
          <Shield className="h-7 w-7 text-gold" />
          <div className="flex flex-col leading-none">
            <span className="font-serif text-lg font-bold text-gold tracking-wide">{t("brand.name")}</span>
            <span className="text-[10px] font-medium text-gold-light/70 tracking-[0.2em] uppercase">{t("brand.sub")}</span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link key={link.href} href={link.href}>
              <span
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  location === link.href || (link.href !== "/" && location.startsWith(link.href))
                    ? "text-gold bg-gold/10"
                    : "text-foreground/80 hover:text-gold hover:bg-gold/5"
                }`}
              >
                {link.label}
              </span>
            </Link>
          ))}
          {isAdmin && (
            <Link href="/admin">
              <span
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  location.startsWith("/admin")
                    ? "text-gold bg-gold/10"
                    : "text-foreground/80 hover:text-gold hover:bg-gold/5"
                }`}
              >
                {t("nav.admin")}
              </span>
            </Link>
          )}
        </nav>

        {/* Desktop CTA */}
        <div className="hidden md:flex items-center gap-2">
          <LanguageSwitcher />
          <a href={buyHref}>
            <Button variant="outline" size="sm" className="border-gold/40 text-gold hover:bg-gold/10 hover:text-gold">
              {t("nav.buyBook")}
            </Button>
          </a>
          {isAuthenticated ? (
            <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground" onClick={() => logout()}>
              <LogOut className="h-4 w-4 mr-1" />
              {t("nav.logout")}
            </Button>
          ) : null}
        </div>

        {/* Mobile Menu */}
        <div className="flex md:hidden items-center gap-1">
          <LanguageSwitcher />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-foreground"
                aria-label={locale === "en" ? "Open menu" : "Abrir menú"}
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-navy border-border w-72">
              <SheetTitle className="sr-only">{t("nav.menu") || "Menu"}</SheetTitle>
              <SheetDescription className="sr-only">{t("nav.menuDesc") || "Navigation menu"}</SheetDescription>
              <nav className="flex flex-col gap-2 mt-8">
                {navLinks.map((link) => (
                  <Link key={link.href} href={link.href} onClick={() => setOpen(false)}>
                    <span
                      className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                        location === link.href
                          ? "text-gold bg-gold/10"
                          : "text-foreground/80 hover:text-gold hover:bg-gold/5"
                      }`}
                    >
                      {link.label}
                    </span>
                  </Link>
                ))}
                {isAdmin && (
                  <Link href="/admin" onClick={() => setOpen(false)}>
                    <span className="block px-4 py-3 rounded-lg text-base font-medium text-foreground/80 hover:text-gold hover:bg-gold/5">
                      {t("nav.admin")}
                    </span>
                  </Link>
                )}
                <div className="border-t border-border my-3" />
                <a href={buyHref} className="px-4">
                  <Button className="w-full bg-gold text-navy-dark hover:bg-gold-light font-semibold">
                    {t("nav.buyBook")}
                  </Button>
                </a>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
